import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  DeviceConfig,
  DeviceLastReadingDto,
  EventValueRange,
  EventValueRangeConfig,
  Port,
  Sensor,
  SensorVisualizationType,
  TimeSeriesData,
} from '@thingslog/repositories';
import { Panel, PanelTitle, ValueRangeLegend, ValueRangeLegendItem } from './base';
import clsx from 'clsx';
import {
  LineProps,
  ReferenceLineProps,
  TooltipPayload,
  TooltipProps,
} from '@thingslog/ui-components';
import { format } from 'date-fns';
import { DateUtil } from '@thingslog/utils';
import { useTranslation } from 'react-i18next';
import InteractiveLineChart from '@thingslog/ui-components/src/Charts/InteractiveLineChart';
import { Styles } from '@thingslog/repositories/src/valueRanges/Styles';
import LineChartStyles from '@thingslog/repositories/src/valueRanges/LineChartStyles';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import { SensorUtils } from '@thingslog/utils';
import { getSensorUnitForVisualization } from '../utils';

const INCLUDE_ZERO_LOCAL_STORAGE_KEY = 'chartYAxisIncludeZero';

export const Chart: FC<ChartProps> = ({
  selectedSensorIndex,
  data: chartData,
  className,
  ports,
  deviceReadings,
  deviceConfig,
  valueRanges,
  dataVisualizationType,
  setDataVisualizationType,
  displayReadingTypeSelector,
}: ChartProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<TimeSeriesData<number>[]>([]);
  const [unit, setUnit] = useState<string>('');
  const [lines, setLines] = useState<Omit<LineProps, 'ref'>[]>([]);
  const [chartTop, setChartTop] = useState<number>(1);
  const [chartBottom, setChartBottom] = useState<number>(0);
  const [referenceLines, setReferenceLines] = useState<ReferenceLineProps[]>([]);
  const [decimalPlaces, setDecimalPlaces] = useState<number>(1);
  const [yAxisWidth, setYAxisWidth] = useState<number>(70);
  const [includeZeroOnYAxis, setIncludeZeroOnYAxis] = useState<boolean>(
    () => localStorage.getItem(INCLUDE_ZERO_LOCAL_STORAGE_KEY) === 'true'
  );
  const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleIncludeZeroOnYAxisChange = (checked: boolean): void => {
    setIncludeZeroOnYAxis(checked);
    localStorage.setItem(INCLUDE_ZERO_LOCAL_STORAGE_KEY, String(checked));
  };

  const legend: ValueRangeLegendItem[] = useMemo(() => {
    if (valueRanges === undefined) return [];
    return valueRanges.rangeEventConfigDtoList
      .filter((valueRanges: EventValueRange) => valueRanges.sensorIndex === selectedSensorIndex)
      .map((valueRange: EventValueRange) => {
        const deviceIconStyles = valueRange.styles.find(
          (style: Styles) => style['@type'] === 'line'
        ) as LineChartStyles | undefined;
        let color = deviceIconStyles?.lineColor ?? '#2A2A2A';
        return {
          name: valueRange.name,
          from: valueRange.min,
          to: valueRange.max,
          color: color,
        };
      });
  }, [valueRanges, selectedSensorIndex]);

  useEffect(() => {
    const wipeData = (): void => {
      setData([]);
      setUnit('');
      setLines([]);
      setChartTop(1);
      setChartBottom(0);
      setYAxisWidth(70);
    };

    if (selectedSensorIndex === null) {
      wipeData();
      return;
    }

    let sensor: Sensor | null = null;
    if (ports !== undefined && selectedSensorIndex !== null && ports[selectedSensorIndex]) {
      sensor = ports[selectedSensorIndex].sensor;
    }

    if (sensor === null) {
      wipeData();
      return;
    }

    const sensorUtils = SensorUtils({ sensor });
    const isOnOffSensor = sensorUtils.isOnOffSensor();
    const unit = getSensorUnitForVisualization(sensor, dataVisualizationType);

    let curve: LineProps['type'] = 'linear';
    if (isOnOffSensor) {
      curve = 'stepAfter';
    }
    let color: string = '#2A2A2A';
    const lastReading = deviceReadings?.find(
      (r: DeviceLastReadingDto) => r.sensorIndex === selectedSensorIndex
    );
    if (lastReading?.rangeEventConfig) {
      const deviceIconStyles = lastReading.rangeEventConfig.styles.find(
        (style: Styles) => style['@type'] === 'line'
      ) as LineChartStyles | undefined;
      if (deviceIconStyles?.lineColor !== undefined) {
        color = deviceIconStyles?.lineColor;
      }
    }

    const numericValues = chartData
      .map((d: TimeSeriesData<number>) => d.value)
      .filter((v: number | unknown): v is number => typeof v === 'number' && Number.isFinite(v));

    if (numericValues.length > 0) {
      const minValue = Math.min(...numericValues);
      const maxValue = Math.max(...numericValues);
      const range = maxValue - minValue;

      let padding: number;
      if (range === 0) {
        const magnitude = Math.abs(maxValue) || 1;
        padding = magnitude * 0.1;
      } else {
        padding = range * 0.15;
      }

      // Zero baseline: pin the axis to 0 on the side the data does not cross;
      // the other side keeps its padded extent, so negative values stay visible.
      let domainMin = includeZeroOnYAxis && minValue >= 0 ? 0 : minValue - padding;
      let domainMax = includeZeroOnYAxis && maxValue <= 0 ? 0 : maxValue + padding;
      if (domainMin === domainMax) {
        // All readings are exactly 0 with the zero baseline on
        domainMax = padding;
      }

      setChartTop(domainMax);
      setChartBottom(domainMin);
      const domainSpan = domainMax - domainMin;
      const calculatedDecimalPlaces = Math.max(1, Math.ceil(-Math.log10(domainSpan)) + 1);
      setDecimalPlaces(calculatedDecimalPlaces);

      // Calculate Y axis width: sample tick values (Recharts generates ~15 ticks)
      const tickFormatter = (v: number): string => `${Number(v.toFixed(calculatedDecimalPlaces))}`;
      let maxTickLength = 0;
      for (let i = 0; i <= 15; i++) {
        const t = domainMin + (i / 15) * domainSpan;
        maxTickLength = Math.max(maxTickLength, tickFormatter(t).length);
      }
      const unitStr = String(unit);
      const unitLen = unitStr && unitStr !== 'NO_UNITS' ? unitStr.length : 0;
      setYAxisWidth(Math.max(90, (maxTickLength + unitLen) * 12 + 10));
    } else {
      setChartTop(1);
      setChartBottom(0);
      setDecimalPlaces(1);
      setYAxisWidth(70);
    }
    setUnit(unit);
    setLines([
      {
        type: curve,
        dataKey: 'value',
        stroke: color,
        strokeWidth: 3,
        activeDot: true,
        dot: chartData.length <= 100,
        isAnimationActive: false,
      },
    ]);

    setData(chartData);
  }, [
    selectedSensorIndex,
    chartData,
    ports,
    deviceReadings,
    dataVisualizationType,
    includeZeroOnYAxis,
  ]);

  useEffect(() => {
    const referenceLines: ReferenceLineProps[] = [];
    if (valueRanges === undefined) {
      setReferenceLines(referenceLines);
      return;
    }

    valueRanges.rangeEventConfigDtoList
      .filter((valueRange: EventValueRange) => valueRange.sensorIndex === selectedSensorIndex)
      .forEach((valueRange: EventValueRange) => {
        const deviceIconStyles = valueRange.styles.find(
          (style: Styles) => style['@type'] === 'line'
        ) as LineChartStyles | undefined;
        let lineColor = deviceIconStyles?.lineColor ?? '#2A2A2A';

        if (valueRange.min !== null) {
          referenceLines.push({
            y: valueRange.min,
            stroke: lineColor,
            label: {
              position: 'insideBottomRight',
              value: `${valueRange.name} - Min`,
              fill: lineColor,
              fontSize: 14,
              style: { fontWeight: 'bold' },
            },
          });
        }
        if (valueRange.max !== null) {
          referenceLines.push({
            y: valueRange.max,
            stroke: lineColor,
            label: {
              position: 'insideTopRight',
              value: `${valueRange.name} - Max`,
              fill: lineColor,
              fontSize: 14,
              style: { fontWeight: 'bold' },
            },
          });
        }
      });
    setReferenceLines(referenceLines);
  }, [valueRanges, selectedSensorIndex]);

  return (
    <Panel className={clsx('space-y-2.5', className)}>
      <Box className="flex flex-wrap items-center justify-between gap-2">
        <PanelTitle title={t('chart')} />
        <Box className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {displayReadingTypeSelector && (
            <Box className="flex items-center">
              <Typography variant="body2" className="mr-2">
                {t('flow')}
              </Typography>
              <Switch
                checked={dataVisualizationType === 'READINGS'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  setDataVisualizationType(e.target.checked ? 'READINGS' : 'FLOW')
                }
                color="primary"
              />
              <Typography variant="body2" className="ml-2">
                {t('raw_readings')}
              </Typography>
            </Box>
          )}
          <IconButton
            size="small"
            aria-label={t('chart_options')}
            onClick={(e: React.MouseEvent<HTMLElement>): void =>
              setOptionsMenuAnchorEl(e.currentTarget)
            }
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={optionsMenuAnchorEl}
            open={Boolean(optionsMenuAnchorEl)}
            onClose={(): void => setOptionsMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={(): void => handleIncludeZeroOnYAxisChange(!includeZeroOnYAxis)}
              selected={includeZeroOnYAxis}
            >
              <ListItemIcon>{includeZeroOnYAxis && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText>{t('y_axis_include_zero')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <ValueRangeLegend items={legend} />
      <section className="h-[clamp(18rem,48vh,32rem)]">
        {data.length > 0 && (
          <InteractiveLineChart
            container={{ width: '100%', height: '100%', debounce: 300 }}
            grid={{ vertical: false, stroke: '#e9e9e9' }}
            tooltip={{
              content: <CustomTooltip unit={unit} />,
            }}
            xAxis={{
              dataKey: 'timestamp',
              tickFormatter: (value: number): string => `${format(value, 'dd/MM HH:mm')}`,
              name: 'Time',
            }}
            yAxis={{
              domain: [chartBottom, chartTop],
              unit: unit,
              tickCount: 15,
              width: yAxisWidth,
              tickMargin: 0,
              axisLine: false,
              tickLine: false,
              tickFormatter: (value: number): string => {
                return `${Number(value.toFixed(decimalPlaces))}`;
              },
              type: 'number',
            }}
            referenceLines={referenceLines}
            lineChart={{
              data: data,
              margin: { left: 8, right: 8, top: 5, bottom: 5 },
            }}
            lines={lines}
          />
        )}
      </section>
    </Panel>
  );
};

const CustomTooltip: FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  unit,
}: CustomTooltipProps) => {
  const { t } = useTranslation();

  if (active && payload && payload.length) {
    return (
      <div className="border border-solid border-slate-600 bg-white p-2 text-sm">
        <h4 className="mt-0 mb-2 text-gray-600">
          {DateUtil.formatTimeZoneAgnosticDate(new Date(label).toString())}
        </h4>
        {payload.map((pld: TooltipPayload<number, string>) => (
          <span className="text-base font-semibold text-gray-700">
            {pld.value} {unit}
          </span>
        ))}
      </div>
    );
  } else {
    return null;
  }
};

interface CustomTooltipProps extends TooltipProps<number, string> {
  unit: string;
}

interface ChartProps {
  selectedSensorIndex: number | null;
  deviceReadings?: DeviceLastReadingDto[]; // for chart color
  data: TimeSeriesData<number>[]; // chart data (flow, readings, or alarms)
  ports?: Record<number, Port>;
  deviceConfig?: DeviceConfig;
  valueRanges?: EventValueRangeConfig;
  className?: string;
  dataVisualizationType: SensorVisualizationType | null;
  setDataVisualizationType: (type: SensorVisualizationType | null) => void;
  displayReadingTypeSelector: boolean;
}
